import { ReactElement, ReactNode, useState } from 'react';

// scroll bar
import 'simplebar/dist/simplebar.css';
// import 'slick-carousel/slick/slick.css';
// import 'slick-carousel/slick/slick-theme.css';
import 'dayjs/locale/en';
import 'dayjs/locale/th';
// apex-chart
import 'styles/apex-chart.css';
import 'styles/react-table.css';

// next
import { NextPage } from 'next';
import { SessionProvider } from 'next-auth/react';
import type { AppProps } from 'next/app';

// third-party
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { BuddhistEraAdapterDayjs } from 'lib/BuddhistEraAdapterDayjs';
import { Provider as ReduxProvider } from 'react-redux';

// project import
import { QueryClientProvider } from '@tanstack/react-query';
import { createAppQueryClient } from 'lib/createAppQueryClient';
import 'lib/env'; // validate env vars at startup

import Snackbar from 'components/@extended/Snackbar';
import ErrorBoundary from 'components/ErrorBoundary';
import Locales from 'components/Locales';
import RTLLayout from 'components/RTLLayout';
import ScrollTop from 'components/ScrollTop';
import { BlockUiProvider } from 'contexts/BlockUiContext';
import { ConfigProvider } from 'contexts/ConfigContext';
import useConfig from 'hooks/useConfig';
import { store } from 'store';
import ThemeCustomization from 'themes';

// types
type LayoutProps = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode;
};

interface Props {
  Component: LayoutProps;
  pageProps: Record<string, unknown>;
}

const LocaleAwareDateProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useConfig();
  const isThai = i18n === 'th';
  return (
    <LocalizationProvider
      dateAdapter={isThai ? BuddhistEraAdapterDayjs : AdapterDayjs}
      adapterLocale={isThai ? 'th' : 'en'}
      localeText={
        isThai
          ? {
              fieldDayPlaceholder: () => 'วว',
              fieldMonthPlaceholder: () => 'ดด',
              fieldYearPlaceholder: () => 'ปปปป'
            }
          : {
              fieldDayPlaceholder: () => 'dd',
              fieldMonthPlaceholder: () => 'mm',
              fieldYearPlaceholder: () => 'yyyy'
            }
      }
    >
      {children}
    </LocalizationProvider>
  );
};

export default function App({ Component, pageProps }: AppProps & Props) {
  const [queryClient] = useState(createAppQueryClient);
  const getLayout = Component.getLayout ?? ((page: ReactElement) => page);

  return (
    <ReduxProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <ConfigProvider>
          <LocaleAwareDateProvider>
            <ThemeCustomization>
              <RTLLayout>
                <Locales>
                  <ScrollTop>
                    <SessionProvider
                      session={pageProps.session}
                      refetchInterval={0}
                      refetchOnWindowFocus={false}
                    >
                      <>
                        <Snackbar />
                        <BlockUiProvider>
                          <ErrorBoundary>{getLayout(<Component {...pageProps} />)}</ErrorBoundary>
                        </BlockUiProvider>
                      </>
                    </SessionProvider>
                  </ScrollTop>
                </Locales>
              </RTLLayout>
            </ThemeCustomization>
          </LocaleAwareDateProvider>
        </ConfigProvider>
      </QueryClientProvider>
    </ReduxProvider>
  );
}
